/**
 * CharCounter — character counter module for the Input and Textarea components (DCMS2)
 *
 * Counts characters typed into an input and displays them as a badge next to the label.
 * Supports states: normal, warning (warning threshold exceeded),
 * error (maximum exceeded). Optionally blocks typing (strict mode).
 *
 * Configured via data attributes on the .dcms-form-group wrapper:
 *   data-char-count-max          — maximum number of characters (required to activate)
 *   data-char-count-warning      — warning threshold (defaults to 85% of max)
 *   data-char-count-strict       — "true" = blocks typing beyond max
 *   data-char-count-warning-color — input background color in the warning state (light mode)
 *   data-char-count-error-color   — input background color in the error state (light mode)
 *   data-char-count-error-message — error message displayed below the input
 *
 * The count result is stored in:
 *   data-char-count              — on the wrapper, current character count (for validators to read)
 *
 * @author DCMS2 Framework
 * @version 1.0.0
 */
export class CharCounter {

    constructor() {
        /** @type {string} Selector for wrappers with an active counter */
        this.wrapperSelector = '[data-char-count-max]';

        /** @type {string} Selector for the counter badge */
        this.badgeSelector = '[data-char-counter-badge]';

        /** @type {WeakSet} Set of already-initialized wrappers (prevents double initialization) */
        this.initialized = new WeakSet();
    }

    /**
     * Initializes the character counter.
     * Attaches to existing elements and observes the DOM for new ones.
     */
    init() {
        // Initialize existing elements
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this._initAll());
        } else {
            this._initAll();
        }

        // MutationObserver — watch for new elements added dynamically
        const observer = new MutationObserver((mutations) => {
            let hasNew = false;
            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType !== 1) continue;
                    if (node.matches && node.matches(this.wrapperSelector)) {
                        hasNew = true;
                        break;
                    }
                    if (node.querySelector && node.querySelector(this.wrapperSelector)) {
                        hasNew = true;
                        break;
                    }
                }
                if (hasNew) break;
            }
            if (hasNew) {
                this._initAll();
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });
    }

    /**
     * Initializes all wrappers with a counter that haven't been handled yet.
     * @private
     */
    _initAll() {
        const wrappers = document.querySelectorAll(this.wrapperSelector);
        for (const wrapper of wrappers) {
            if (!this.initialized.has(wrapper)) {
                this._attach(wrapper);
            }
        }
    }

    /**
     * Attaches the counter to a specific wrapper.
     * @param {HTMLElement} wrapper — the .dcms-form-group element with data-char-count-max
     * @private
     */
    _attach(wrapper) {
        const input = wrapper.querySelector('input, textarea');
        if (!input) return;

        this.initialized.add(wrapper);

        const badge = wrapper.querySelector(this.badgeSelector);
        const config = this._getConfig(wrapper);

        // Initial update (value from the server)
        this._update(input, badge, wrapper, config);

        // Event listener on 'input' — works with keyboard, paste, drag, autofill
        input.addEventListener('input', () => {
            this._update(input, badge, wrapper, config);
        });
    }

    /**
     * Reads the configuration from the wrapper's data attributes.
     * @param {HTMLElement} wrapper
     * @returns {Object} Counter configuration
     * @private
     */
    _getConfig(wrapper) {
        const max = parseInt(wrapper.dataset.charCountMax, 10);
        let warning = wrapper.dataset.charCountWarning
            ? parseInt(wrapper.dataset.charCountWarning, 10)
            : null;

        // Default warning = 85% of max
        if (warning === null && !isNaN(max)) {
            warning = Math.floor(max * 0.85);
        }

        return {
            max: isNaN(max) ? null : max,
            warning: warning,
            strict: wrapper.dataset.charCountStrict === 'true',
            warningColor: wrapper.dataset.charCountWarningColor || '#FFF9C4',
            errorColor: wrapper.dataset.charCountErrorColor || '#FFCDD2',
            errorMessage: wrapper.dataset.charCountErrorMessage || ''
        };
    }

    /**
     * Updates the counter state: badge, background color, data attribute, error message.
     * @param {HTMLInputElement} input
     * @param {HTMLElement|null} badge
     * @param {HTMLElement} wrapper
     * @param {Object} config
     * @private
     */
    _update(input, badge, wrapper, config) {
        let count = input.value.length;

        // Strict mode — truncate text to max
        if (config.strict && config.max !== null && count > config.max) {
            // Preserve cursor position
            const cursorPos = input.selectionStart;
            input.value = input.value.substring(0, config.max);
            count = config.max;
            // Restore cursor (at the end of the truncated text)
            const newPos = Math.min(cursorPos, config.max);
            input.setSelectionRange(newPos, newPos);
        }

        // Update data-char-count on the wrapper (for validators to read)
        wrapper.dataset.charCount = count;

        // Update badge
        if (badge) {
            badge.textContent = count;
            this._updateBadgeState(badge, count, config);
        }

        // Update input background color
        this._updateInputBackground(input, count, config);

        // Update error message
        this._updateErrorMessage(wrapper, count, config);
    }

    /**
     * Updates the badge's appearance (background and text color) depending on the state.
     * @param {HTMLElement} badge
     * @param {number} count
     * @param {Object} config
     * @private
     */
    _updateBadgeState(badge, count, config) {
        // Remove previous state classes
        badge.classList.remove(
            'dcms-char-counter-badge--normal',
            'dcms-char-counter-badge--warning',
            'dcms-char-counter-badge--error'
        );

        if (config.max !== null && count >= config.max) {
            badge.classList.add('dcms-char-counter-badge--error');
        } else if (config.warning !== null && count >= config.warning) {
            badge.classList.add('dcms-char-counter-badge--warning');
        } else {
            badge.classList.add('dcms-char-counter-badge--normal');
        }
    }

    /**
     * Updates the input's background color depending on the state.
     * Supports dark mode by checking the 'dark' class on <html>.
     * @param {HTMLInputElement} input
     * @param {number} count
     * @param {Object} config
     * @private
     */
    _updateInputBackground(input, count, config) {
        if (config.max === null) {
            // No limit — don't change the background
            input.style.backgroundColor = '';
            return;
        }

        const isDark = document.documentElement.classList.contains('dark');

        if (count >= config.max) {
            // Error state
            input.style.backgroundColor = isDark ? 'rgba(127, 29, 29, 0.3)' : config.errorColor;
        } else if (config.warning !== null && count >= config.warning) {
            // Warning state
            input.style.backgroundColor = isDark ? 'rgba(120, 53, 15, 0.2)' : config.warningColor;
        } else {
            // Normal state — restore the default background
            input.style.backgroundColor = '';
        }
    }

    /**
     * Updates the error message below the input.
     * Uses the existing .help and #ID-helpText container.
     * @param {HTMLElement} wrapper
     * @param {number} count
     * @param {Object} config
     * @private
     */
    _updateErrorMessage(wrapper, count, config) {
        if (!config.errorMessage) return;

        const fieldId = wrapper.dataset.dcmsFieldId;
        if (!fieldId) return;

        const helpContainer = document.getElementById(fieldId + '-help');
        const helpText = document.getElementById(fieldId + '-helpText');

        if (!helpContainer || !helpText) return;

        if (config.max !== null && count >= config.max) {
            helpText.textContent = config.errorMessage;
            helpText.classList.remove('validationSuccessMessage');
            helpText.classList.add('validationErrorMessage');
            helpContainer.style.display = 'block';
        } else {
            // Only clear if it's our message (don't override Parsley validation)
            if (helpText.textContent === config.errorMessage) {
                helpText.textContent = '';
                helpText.classList.remove('validationErrorMessage');
                helpContainer.style.display = 'none';
            }
        }
    }
}
